/**
 * src/engine/assetClasses.ts — illustrative per-asset-class defaults (§6.21).
 *
 * Advance rates are approximations: Schwab assigns real advance rates per security via
 * its Advance Rate Lookup. These seed a new bucket and are fully overridable in the UI.
 */
import type { AssetClass, CollateralBucket } from '@/types';

export interface AssetClassDefault {
  label: string;
  defaultAdvanceRate: number;
  /** 1 = most liquid; drives the schwab_liquidity liquidation order (§6.22). */
  liquidityRank: number;
  defaultReturnPct: number;
  defaultVolatilityPct: number;
}

export const ASSET_CLASS_DEFAULTS: Record<AssetClass, AssetClassDefault> = {
  cash_mmf: { label: 'Cash & money-market', defaultAdvanceRate: 0.95, liquidityRank: 1, defaultReturnPct: 4, defaultVolatilityPct: 0 },
  treasury: { label: 'U.S. Treasuries', defaultAdvanceRate: 0.92, liquidityRank: 2, defaultReturnPct: 4, defaultVolatilityPct: 4 },
  broad_etf_large_cap: { label: 'Broad ETF / large-cap equity', defaultAdvanceRate: 0.7, liquidityRank: 2, defaultReturnPct: 7, defaultVolatilityPct: 15 },
  equity: { label: 'Other equity', defaultAdvanceRate: 0.65, liquidityRank: 3, defaultReturnPct: 7, defaultVolatilityPct: 22 },
  mutual_fund: { label: 'Mutual fund', defaultAdvanceRate: 0.65, liquidityRank: 3, defaultReturnPct: 7, defaultVolatilityPct: 13 },
  fixed_income: { label: 'Fixed income (corp/muni)', defaultAdvanceRate: 0.85, liquidityRank: 4, defaultReturnPct: 5, defaultVolatilityPct: 6 },
  illiquid_restricted: { label: 'Illiquid / restricted', defaultAdvanceRate: 0.4, liquidityRank: 5, defaultReturnPct: 7, defaultVolatilityPct: 25 },
  other: { label: 'Other / ineligible', defaultAdvanceRate: 0, liquidityRank: 5, defaultReturnPct: 0, defaultVolatilityPct: 0 },
};

/** Seed a new collateral bucket from an asset class (§8.1). Values are editable. */
export function bucketFromAssetClass(assetClass: AssetClass, value = 0): CollateralBucket {
  const d = ASSET_CLASS_DEFAULTS[assetClass];
  return {
    id: crypto.randomUUID(),
    label: d.label,
    assetClass,
    value,
    advanceRate: d.defaultAdvanceRate,
    eligible: assetClass !== 'other',
    expectedReturnAnnualPct: d.defaultReturnPct,
    volatilityPct: d.defaultVolatilityPct,
    dividendYieldAnnualPct: 0,
    costBasis: value,
  };
}
